import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X } from "lucide-react";
import { EASE } from "../lib/motion";
import { useSearch } from "../lib/search";
import api from "../lib/api";

const QUICK = ["OVERSIZED", "TEE", "BLACK", "BOXY"];

export default function SearchOverlay() {
  const { open, closeSearch, initialQuery } = useSearch();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQ(initialQuery || "");
      setResults(null);
      setLoading(false);
      const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 120);
      document.body.style.overflow = "hidden";
      return () => { clearTimeout(t); document.body.style.overflow = ""; };
    }
    document.body.style.overflow = "";
    return undefined;
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closeSearch(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeSearch]);

  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (!term) { setResults(null); setLoading(false); return undefined; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/products", { params: { q: term } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = (slug) => { closeSearch(); navigate(`/product/${slug}`); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          className="fixed inset-0 z-[90] bg-[rgba(6,7,9,0.97)] backdrop-blur-xl overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: EASE }}
        >
          <div className="max-w-3xl mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-16">
            <div className="flex items-center justify-between mb-6">
              <p className="font-mono text-[11px] tracking-[0.3em] text-volt">SEARCH THE DROP</p>
              <button data-testid="search-overlay-close-btn" onClick={closeSearch} aria-label="Close search" className="text-dim hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="flex items-center gap-3 border-b border-white/20 pb-4 focus-within:border-acid transition-colors duration-300">
              <Search size={22} className="text-dim shrink-0" />
              <input
                ref={inputRef}
                data-testid="search-overlay-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="SEARCH PRODUCTS, COLORS, FITS…"
                className="w-full bg-transparent font-display text-xl md:text-3xl font-bold uppercase tracking-tight text-ink placeholder:text-mute placeholder:font-mono placeholder:text-sm md:placeholder:text-base placeholder:tracking-[0.2em] outline-none"
              />
              {q && (
                <button data-testid="search-overlay-clear-btn" onClick={() => setQ("")} aria-label="Clear search" className="text-dim hover:text-white transition-colors shrink-0">
                  <X size={16} />
                </button>
              )}
            </div>

            {!q.trim() && (
              <div data-testid="search-idle-hint" className="pt-10">
                <p className="font-mono text-[11px] tracking-[0.25em] text-mute mb-4">QUICK SEARCH</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK.map((w) => (
                    <button
                      key={w}
                      data-testid={`search-quick-chip-${w.toLowerCase()}`}
                      onClick={() => setQ(w)}
                      className="font-mono text-[11px] tracking-[0.2em] border border-white/15 px-4 py-2 text-dim hover:border-acid hover:text-acid transition-colors duration-300"
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loading && (
              <p data-testid="search-loading" className="font-mono text-[11px] tracking-widest text-mute pt-10">SCANNING THE RACK…</p>
            )}

            {!loading && results !== null && (
              <>
                <p data-testid="search-results-count" className="font-mono text-[11px] tracking-widest text-mute pt-8">
                  {String(results.length).padStart(2, "0")} UNIT{results.length === 1 ? "" : "S"} FOUND
                </p>
                {results.length === 0 ? (
                  <div data-testid="search-no-results" className="pt-12 text-center">
                    <p className="font-display text-xl md:text-2xl font-bold uppercase">NOTHING IN THE DROP MATCHES "{q.trim().toUpperCase()}"</p>
                    <p className="font-mono text-[11px] tracking-widest text-mute mt-3">TRY A COLOR, A FIT OR A FABRIC</p>
                  </div>
                ) : (
                  <ul className="mt-6 divide-y divide-white/[0.08] border-t border-white/[0.08]">
                    {results.map((p) => {
                      const save = p.original_price && p.original_price > p.price ? p.original_price - p.price : 0;
                      return (
                        <li key={p._id}>
                          <button
                            data-testid={`search-result-${p.code}`}
                            onClick={() => go(p.slug)}
                            className="w-full flex items-center gap-4 py-4 text-left group"
                          >
                            <img src={p.image} alt={p.title} className="w-14 h-[70px] object-cover shrink-0 border border-white/[0.08]" />
                            <div className="flex-1 min-w-0">
                              <p className="font-display text-sm font-bold uppercase group-hover:text-acid transition-colors duration-300 truncate">{p.title}</p>
                              <p className="font-mono text-[10px] text-dim tracking-widest mt-1 uppercase truncate">{p.tag || p.gsm}</p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-mono text-sm font-bold text-acid">₹{p.price.toLocaleString("en-IN")}</p>
                              {save > 0 && (
                                <p data-testid={`search-result-save-${p.code}`} className="font-mono text-[10px] font-bold text-volt mt-1 tracking-widest">SAVE ₹{save.toLocaleString("en-IN")}</p>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
